import Button from "@rio-cloud/rio-uikit/components/button/Button";
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router";

import AnimatedFlow from '@/components/molecule/AnimatedFlow/AnimatedFlow';
import {ButtonContainer, TextContainer} from "@/components/organism/WhatIsFleetAssistant/WhatIsFleetAssistant.styles";
import { EAppRoutes } from "@/routes/routes";
import { setCurrentPage } from '@/store/about/aboutSlice';
import {setCurrentLayout} from "@/store/layout/layoutSlice";

import PageNavigation from '../PageNavigation/PageNavigation';

const texts = {
  intro: 'getStartedPage.howAssistantWorksPrompt.introduction',
  steps: [
    {
      title: 'getStartedPage.howAssistantWorksPrompt.step_one.title',
      description: 'getStartedPage.howAssistantWorksPrompt.step_one.description',
    },
    {
      title: 'getStartedPage.howAssistantWorksPrompt.step_two.title',
      description: 'getStartedPage.howAssistantWorksPrompt.step_two.description',
    },
    {
      title: 'getStartedPage.howAssistantWorksPrompt.step_three.title',
      description: 'getStartedPage.howAssistantWorksPrompt.step_three.description',
    },
    {
      title: 'getStartedPage.howAssistantWorksPrompt.step_four.title',
      description: 'getStartedPage.howAssistantWorksPrompt.step_four.description',
    },
    {
      title: 'getStartedPage.howAssistantWorksPrompt.step_five.title',
      description: 'getStartedPage.howAssistantWorksPrompt.step_five.description',
    },
  ],
  startChat: {
    label: 'getStartedPage.whatIsFleetAssistant.startChatLabel',
    button: 'getStartedPage.common.startChat.label',
  },
  learnMore: {
    label: 'getStartedPage.getStartedSection.learnMore.label',
  },
  visualizationSection: {
    title: 'getStartedPage.howAssistantWorksPrompt.diagram.title',
  },
  pageNavigation: {
    previousLabel: 'getStartedPage.whatIsFleetAssistant.label',
    nextLabel: 'getStartedPage.frequentlyAskedQuestions.label',
  },
};

const classes = {
  container: 'panel panel-default panel-body panel-blank padding-x-25 margin-top-25',
  introText: 'line-height-150rel',
  timeline: 'timeline margin-top-50',
  timelineElement: 'timeline-element margin-bottom-0',
  timelineDate: 'timeline-date timeline-date-small timeline-date-thin margin-right-10 margin-top-5',
  timelineDot: 'width-10 aspect-ratio-1 rounded-circle border border-color-info border-width-2 bg-white margin-left-1',
  timelineContent: 'timeline-content margin-bottom-20 text-color-darker',
  section: 'margin-top-40 margin-bottom-20',
  sectionTitle: 'text-size-h3 text-bold margin-bottom-10',
  sectionDescription: 'line-height-150rel',
  imageContainer: 'text-center margin-bottom-20',
  image: 'max-width-90pct bg-darkest padding-y-25 padding-x-50',
};

const HowAssistantWorks = () => {
  const dispatch = useDispatch();
  const componentTopRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleStartConversation = () => {
    dispatch(setCurrentLayout('chat'));
    navigate(EAppRoutes.CHAT);
  };

  const scrollFunction = () => {
    if (componentTopRef.current) {
      componentTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pageTopRef = useRef<HTMLDivElement>(null);

  const handlePreviousSection = () => dispatch(setCurrentPage('page2'));
  const handleNextSection = () => dispatch(setCurrentPage('page4'));

  useEffect(() => {
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className={classes.container}>
      <div ref={pageTopRef} style={{ position: 'absolute', top: '0' }} />
      <p className={classes.introText}>
        <FormattedMessage id={texts.intro} />
      </p>

      <ButtonContainer align="flex-start">
        <Button bsStyle={Button.PRIMARY} onClick={handleStartConversation}>
          <FormattedMessage id={texts.startChat.button} />
        </Button>
        <Button bsStyle={Button.DEFAULT} onClick={scrollFunction}>
          <FormattedMessage id={texts.learnMore.label} />
        </Button>
      </ButtonContainer>

      <div className={classes.timeline}>
        {texts.steps.map((step, index) => (
          <div key={index} className={classes.timelineElement}>
            <div className={classes.timelineDate}>
              <div className={classes.timelineDot} />
            </div>
            <div className={classes.timelineContent}>
              <strong>
                <FormattedMessage id={step.title} />
              </strong>
              <p>
                <FormattedMessage id={step.description} />
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className={classes.section} ref={componentTopRef}>
        <h3 className={classes.sectionTitle}>
          <FormattedMessage id={texts.visualizationSection.title} />
        </h3>
        <AnimatedFlow />
      </section>

      <TextContainer>
        <p>
          <FormattedMessage id={texts.startChat.label} />
        </p>
        <ButtonContainer align="center">
          <Button bsStyle={Button.PRIMARY} onClick={handleStartConversation}>
            <FormattedMessage id={texts.startChat.button} />
          </Button>
        </ButtonContainer>
      </TextContainer>

      <PageNavigation
        onNext={handleNextSection}
        onPrevious={handlePreviousSection}
        previousLabel={texts.pageNavigation.previousLabel}
        nextLabel={texts.pageNavigation.nextLabel}
      />
    </div>
  );
};

export default HowAssistantWorks;
